/* eslint-disable no-nested-ternary */
import PropTypes from 'prop-types';
import { Avatar, Box, Card, IconButton, Typography, useTheme } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PhoneIcon from '@mui/icons-material/Phone';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PhoneMissedIcon from '@mui/icons-material/PhoneMissed';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import SmsIcon from '@mui/icons-material/Sms';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import PhoneDisabledIcon from '@mui/icons-material/PhoneDisabled';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import moment from 'moment';
import { alpha } from '@mui/material/styles';
import { getTone } from '../../../theme/tones';

const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric' };

const CustomerRelanceComment = (props) => {
  const { createdAt, comment, source, id, handleDelete, qualification, recallDate, ...other } = props;
  const theme = useTheme();

  // Color mapping based on qualification
  const getQualificationColor = (qualif) => {
    switch (qualif) {
      case 'Suivi - Mail':
      case 'Suivi - SMS':
      case 'Suivi - Téléphone':
        return getTone('emerald').base; // Green
      case 'Annulation':
        return getTone('rose').base; // Red
      case 'Décalage':
      case 'Pas répondu':
        return getTone('neutral').base; // Gray/Slate
      case 'À rappeler':
        return getTone('orange').base; // Orange
      case 'Souhaite un email':
        return getTone('violet').base; // Purple
      case 'Va se connecter':
        return getTone('amber').base; // Yellow
      case 'Numéro incorrect':
        return getTone('blue').base; // Blue
      default:
        return getTone('neutral').base;
    }
  };

  const qualifColor = getQualificationColor(qualification);

  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
        p: 2.5,
        '& + &': {
          mt: 2
        },
        borderRadius: '20px',
        border: '1px solid',
        borderLeft: `5px solid ${qualifColor}`,
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        backdropFilter: 'blur(20px)',
        boxShadow: (theme) => `0 4px 16px ${alpha(theme.palette.common.black, 0.01)}`,
        transition: 'transform 0.2s ease',
        '&:hover': {
          transform: 'translateX(2px)'
        }
      }}
      {...other}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, width: '100%' }}>
        <Avatar
          sx={{
            bgcolor: 'action.hover',
            width: 36,
            height: 36,
            mr: 1.5,
            color: qualifColor
          }}
        >
          { qualification === 'Suivi - Mail' && <MailOutlineIcon sx={{ fontSize: 20 }} /> }
          { qualification === 'Suivi - SMS' && <SmsIcon sx={{ fontSize: 20 }} /> }
          { qualification === 'Suivi - Téléphone' && <PhoneAndroidIcon sx={{ fontSize: 20 }} /> }
          { qualification === 'Décalage' && <CalendarTodayIcon sx={{ fontSize: 18 }} /> }
          { qualification === 'À rappeler' && <PhoneIcon sx={{ fontSize: 20 }} /> }
          { qualification === 'Souhaite un email' && <MailOutlineIcon sx={{ fontSize: 20 }} /> }
          { qualification === 'Annulation' && <HighlightOffIcon sx={{ fontSize: 20 }} /> }
          { qualification === 'Va se connecter' && <HourglassEmptyIcon sx={{ fontSize: 20 }} /> }
          { qualification === 'Numéro incorrect' && <PhoneDisabledIcon sx={{ fontSize: 20 }} /> }
          { qualification === 'Pas répondu' && <PhoneMissedIcon sx={{ fontSize: 20 }} /> }
        </Avatar>
        <Box sx={{ flexGrow: 1 }}>
          <Typography
            color="text.primary"
            variant="subtitle2"
            sx={{ fontWeight: 800 }}
          >
            {source}
          </Typography>
          
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              px: 1.2,
              py: 0.2,
              borderRadius: '8px',
              fontSize: '0.72rem',
              fontWeight: 700,
              backgroundColor: 'action.hover',
              color: qualifColor,
              mt: 0.25
            }}
          >
            {`${qualification} ${recallDate === undefined || recallDate === null ? '' : `• Rappel le ${moment(recallDate).format('DD/MM/YYYY à H:mm')}`}`}
          </Box>
        </Box>
        <IconButton
          size="small"
          onClick={() => handleDelete(id)}
          sx={{ 
            ml: 1,
            color: 'text.secondary',
            '&:hover': {
              color: 'error.main',
              backgroundColor: (theme) => alpha(theme.palette.error.main, 0.05)
            }
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>

      <Typography
        color="text.secondary"
        variant="body2"
        sx={{ 
          mt: 1, 
          pl: 6,
          lineHeight: 1.6,
          fontWeight: 500
        }}
      >
        {comment}
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
        <Typography
          color="text.secondary"
          sx={{ fontSize: '0.7rem', fontStyle: 'italic', fontWeight: 500 }}
        >
          {new Date(createdAt).toLocaleDateString('fr-FR', options)}
        </Typography>
      </Box>
    </Card>
  );
};

CustomerRelanceComment.propTypes = {
  createdAt: PropTypes.string.isRequired,
  comment: PropTypes.string.isRequired,
  source: PropTypes.string.isRequired,
  handleDelete: PropTypes.func.isRequired,
  id: PropTypes.number.isRequired,
  qualification: PropTypes.string,
  recallDate: PropTypes.string
};

export default CustomerRelanceComment;
